import { useCallback, useState } from 'react';
import { NotificationTypes } from 'src/enums/Notifications';
import { ChatMessage } from 'src/interfaces/Chat';
import {
  addSessions,
  appendMessageList,
  removeLastMessages,
  removeSession,
  setEmptyChatSuggestedQuestions,
  setIsCreatingNewSession,
  setQuestionMode,
  setSelectedSession,
  setSessions,
  setSessionsLoading,
  streamChatMessage,
  updateChatList,
  updateChatLoading,
  updateStreaming,
} from 'src/redux/redux-store';
import * as chatActions from 'src/redux/redux-store/chat/chatReducer';
import { store } from 'src/redux/redux-store/configureStore';
import { useAppDispatch, useAppSelector } from 'src/redux/redux-store/hooks';
import { ErrorKind, ResponseKind } from 'src/services/services/MainService';
import { chatService } from 'src/services/services/chat/chat.api';
import { userService } from 'src/services/services/user/user.api';
import { isAskModeJSON } from 'src/utils/helpers';
import { sendFailureNotification } from 'src/utils/notifications';

export const useChat = () => {
  const dispatch = useAppDispatch();
  const chatLoading = useAppSelector((state) => state.chat.loading);
  let selectedSessionId = useAppSelector((state) => state.chat.selectedSession);
  const streaming = useAppSelector((state) => state.chat.streaming);
  const messages = useAppSelector((state) => state.chat).messages;
  const question_mode = useAppSelector((state) => state.chat.questionMode);
  const tracelessMessages = useAppSelector(
    (state) => state.chat
  ).tracelessMessages;
  const activeSubject = useAppSelector((state) => state.chat.activeSubject);
  const [inputMessage, setInputMessage] = useState('');

  // Subjects fetched from backend
  const subjects = useAppSelector((state) => state.chat.subjects);
  const subjectsLoading = useAppSelector((state) => state.chat.subjectsLoading);

  // Fetch subjects for child dashboard
  const fetchSubjects = useCallback(
    async (force = false) => {
      if ((subjects.length > 0 && !force) || subjectsLoading) return;

      try {
        dispatch(chatActions.setSubjectsLoading(true));
        const response = await userService.getAllSubjects();

        if (response.kind === ResponseKind.OK && response.data) {
          const fetched: any = response.data;
          const subjectNames: string[] = fetched.map((item: any) =>
            typeof item === 'string' ? item : item.name
          );

          dispatch(chatActions.setSubjects(subjectNames));

          // Ensure an active subject is set if none is set
          if (subjectNames.length > 0 && !activeSubject) {
            dispatch(chatActions.setActiveSubject(subjectNames[0]));
          }
          return subjectNames;
        } else {
          throw new Error('Failed to fetch subjects');
        }
      } catch (e) {
        console.error('Error fetching subjects:', e);
        throw e;
      } finally {
        dispatch(chatActions.setSubjectsLoading(false));
      }
    },
    [dispatch, subjects.length, subjectsLoading, activeSubject]
  );

  const setActiveSubject = useCallback(
    (subject: string) => {
      dispatch(chatActions.setActiveSubject(subject));
    },
    [dispatch]
  );

  const selectedLevel = useAppSelector(
    (state) => state.parentChat?.selectedLevel
  );

  const getSessions = useCallback(async () => {
    try {
      dispatch(setSessionsLoading(true));

      // For parent users, include the selected level in the request
      const queryParams = new URLSearchParams();
      queryParams.append('subject', activeSubject || '');
      if (selectedLevel) {
        queryParams.append('level_id', selectedLevel);
      }

      const queryString = queryParams.toString();
      const response = await chatService.getAllSessions(queryString);
      console.log('Response for sessions = ', response);

      if (response.kind == ResponseKind.OK && response.data?.chat_sessions) {
        dispatch(setSessions(response.data.chat_sessions));
        const currentSessionId = selectedSessionId?.id;
        const firstSession = response.data.chat_sessions[0];
        if (
          !currentSessionId ||
          !response.data.chat_sessions.some((s) => s.id === currentSessionId)
        ) {
          dispatch(setSelectedSession(firstSession));
          return firstSession;
        }
        return (
          response.data.chat_sessions.find((s) => s.id === currentSessionId) ||
          firstSession
        );
        // }
      } else {
        throw new Error('Failed to retrieve chat sessions');
      }
    } catch (e) {
      sendFailureNotification(
        NotificationTypes.chatSession,
        'Failed to retrieve or create chat sessions'
      );
      console.log('Error in getSessions = ', e);
      throw e; // Re-throw to handle in the component
    } finally {
      dispatch(setSessionsLoading(false));
    }
  }, [activeSubject, selectedSessionId?.id, selectedLevel]);

  const newSession = useCallback(
    async (level_id?: string) => {
      const session_title = `New Session`;
      // Use the provided level_id or fall back to the selected level from parent chat state
      const levelIdToUse = level_id || selectedLevel || undefined;
      dispatch(setIsCreatingNewSession(true));
      const response = await chatService.createNewSession(
        session_title,
        activeSubject || '',
        levelIdToUse
      );
      if (response.kind == ResponseKind.OK && response.data) {
        dispatch(addSessions(response.data));
        dispatch(setSelectedSession(response.data));
        dispatch(setIsCreatingNewSession(false));
        return response.data; // Return the created session
      } else {
        sendFailureNotification(
          NotificationTypes.chatSession,
          'Failed to create session'
        );
        dispatch(setIsCreatingNewSession(false));
        return null;
      }
    },
    [activeSubject, selectedLevel]
  );

  const deleteSession = useCallback(
    async (session_id: string) => {
      const response = await chatService.deleteSession(session_id);
      if (response.kind == ResponseKind.OK) {
        if (selectedSessionId.id == session_id) {
          dispatch(removeSession(session_id));
          newSession();
        } else {
          dispatch(removeSession(session_id));
        }
      } else {
        sendFailureNotification(
          NotificationTypes.chatSession,
          'Failed to delete sessions'
        );
      }
    },
    [selectedSessionId]
  );

  const suggestQuestion = useCallback(
    async (check: boolean, session_id: string) => {
      try {
        dispatch(setQuestionMode(false));
        setInputMessage('');

        if (check) {
          dispatch(
            appendMessageList({
              role: 'ai',
              content: '',
            })
          );
        }
        const response = await chatService.getSuggestedQuestion(
          check,
          session_id
        );

        if (response.kind == ResponseKind.OK && response.data) {
          let stream = response.data.ai_response;

          if (check) {
            dispatch(streamChatMessage(stream));
          }
        }
      } catch (e) {
      } finally {
        dispatch(updateStreaming(false));
      }
    },
    [selectedSessionId]
  );

  const triggerMessageForFileUpload = (message: string, file: File) => {
    let url = URL.createObjectURL(file);
    let JsonMessage;
    if (file.type == 'application/pdf') {
      JsonMessage = {
        file_name: file.name,
      };
    } else {
      JsonMessage = {
        image_url: url,
      };
    }

    dispatch(updateStreaming(true));
    setInputMessage('');

    dispatch(
      appendMessageList({
        role: 'human',
        content: JSON.stringify(JsonMessage),
      })
    );

    dispatch(
      appendMessageList({
        role: 'human',
        content: message,
      })
    );

    dispatch(
      appendMessageList({
        role: 'ai',
        content: '',
      })
    );
  };

  const triggerMessageForFileUploadError = (message: string) => {
    // Remove the last three messages that were added during the upload attempt
    dispatch(removeLastMessages(3));

    // Reset streaming state and input message
    dispatch(updateStreaming(false));
    setInputMessage(message);

    // Optionally, you can add an error message to the chat
    dispatch(
      appendMessageList({
        role: 'ai',
        content: 'File upload failed. Please try again.',
      })
    );
  };

  const triggerMessageForMessageLimitError = (
    message: string,
    read_url: string | undefined
  ) => {
    // Remove the last three messages that were added during the upload attempt
    if (read_url) {
      dispatch(removeLastMessages(3));
    } else {
      dispatch(removeLastMessages(2));
    }

    // Reset streaming state and input message
    dispatch(updateStreaming(false));
    setInputMessage(message);

    // Optionally, you can add an error message to the chat
    dispatch(
      appendMessageList({
        role: 'ai',
        content:
          'You have reached the maximum limit for messages. Please try again after a while',
      })
    );
  };

  const sendMessage = async (message: string, read_url: string | undefined) => {
    try {
      if (!read_url) {
        dispatch(updateStreaming(true));
        const trimmedMessage = message.trim();

        setInputMessage('');
        if (!trimmedMessage || trimmedMessage === '\n') {
          return;
        }

        dispatch(
          appendMessageList({
            role: 'human',
            content: message,
          })
        );

        dispatch(
          appendMessageList({
            role: 'ai',
            content: '',
          })
        );
      }

      // Get the current level, level_name, and subject from Redux store
      const state = store.getState();
      // Get the full level object to access the name
      const selectedLevelObj = state.parentChat?.levels?.find(
        (l) => l.id === state.parentChat?.selectedLevel
      );
      const level_name = selectedLevelObj?.name; // Get the name from the selected level
      const subject = state.chat.activeSubject; // Active subject from chat state

      const response = await chatService.sendMessage(
        message,
        read_url,
        selectedSessionId.id,
        level_name || undefined, // Pass level_name as level
        subject || undefined, // Ensure we pass undefined instead of null
        undefined // Don't pass level_id anymore
      );

      if (response.kind == ResponseKind.OK && response.data) {
        let stream = response.data.ai_response;

        if (isAskModeJSON(stream)) {
          dispatch(setQuestionMode(true));
        }

        for (let i = 0; i < stream.length; i++) {
          dispatch(streamChatMessage(stream[i]));
        }
      } else if (response.kind == 'too-many-requests') {
        triggerMessageForMessageLimitError(message, read_url);
      }
    } catch (e) {
    } finally {
      dispatch(updateStreaming(false));

      // if(messages.length == 1){
      //   const res = await chatService.updateSessionTitle(message, selectedSessionId.id)

      //   if(res.kind == ResponseKind.OK){
      //     updateSessionTitle(message, selectedSessionId.id)
      //   }
      // }

      // if(file){
      //   getMessages(selectedSessionId.id)
      // }
      // getSuggestedQuestions()
    }
  };

  const getMessages = async (sessionId: string) => {
    try {
      if (!sessionId) {
        console.log('No session ID provided to getMessages');
        return;
      }

      // console.log('Fetching messages for session:', sessionId);
      dispatch(updateChatLoading(true));
      // console.log('from child', sessionId);

      const response = await chatService.getAllMessages(sessionId);

      if (response.kind == ResponseKind.OK) {
        const messages = response?.data?.messages;
        const curriculum_questions = response?.data?.curriculum_questions || [];
        dispatch(setEmptyChatSuggestedQuestions(curriculum_questions));
        const tempMessages: ChatMessage[] = [];

        if (messages != undefined) {
          dispatch(updateChatList([...tempMessages, ...messages]));
        }
      }
    } catch (e) {
    } finally {
      dispatch(updateChatLoading(false));
    }
  };

  const getChildSessionQuestion = async (
    session_id: string,
    level: string,
    subject: string
  ) => {
    dispatch(updateStreaming(true));
    try {
      const response = await chatService.fetchChildSessionQuestion(
        session_id,
        level,
        subject
      );
      console.log('FETCH RES', response);
      dispatch(
        appendMessageList({
          role: 'ai',
          content: response.text,
          image_url: response.image_url,
        })
      );
    } catch (e) {
      return { kind: ErrorKind.BAD_DATA_ERROR };
    } finally {
      dispatch(updateStreaming(false));
    }
  };

  return {
    getMessages,
    sendMessage,
    chatLoading,
    getSessions,
    newSession,
    triggerMessageForFileUpload,
    deleteSession,
    messages,
    suggestQuestion,
    tracelessMessages,
    streaming,
    question_mode,
    triggerMessageForMessageLimitError,
    triggerMessageForFileUploadError,
    inputMessage,
    setInputMessage,
    activeSubject,
    setActiveSubject,
    subjects,
    fetchSubjects,
    getChildSessionQuestion,
  };
};
